import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LogOut, BookOpen, Calendar, Contact2, Bell, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { useAuth } from "@/components/auth/AuthProvider";
import { collection, query, where, onSnapshot } from "firebase/firestore";

interface StudentSidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const StudentSidebar: React.FC<StudentSidebarProps> = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    if (!auth.currentUser) return;

    // Set up real-time listener for student-specific unread notifications
    const notificationsQuery = query(
      collection(db, "notifications"),
      where("userId", "==", auth.currentUser.uid),
      where("read", "==", false)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      setUnreadNotifications(snapshot.docs.length);
    }, (error) => {
      console.error("Error fetching notification count:", error);
      setUnreadNotifications(0); // Set to 0 on error to avoid UI issues
    });

    return () => unsubscribe();
  }, []);

  const navItems = [
    { name: "Courses", value: "courses", icon: <BookOpen className="h-5 w-5" /> },
    { name: "Preferences", value: "preferences", icon: <Settings className="h-5 w-5" /> },
    { name: "Schedule", value: "schedule", icon: <Calendar className="h-5 w-5" /> },
    { 
      name: "Notifications", 
      value: "notifications", 
      icon: <Bell className="h-5 w-5" />,
      badge: unreadNotifications > 0 ? unreadNotifications : null
    },
  ];

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };
  return (
    <div className="h-full w-64 bg-background border-r p-4 flex flex-col">
      <div className="mb-8">
        <h2 className="text-2xl font-bold">MSS Driving School</h2>
        <p className="text-muted-foreground text-sm">Student Portal</p>
      </div>
      <nav className="space-y-1 flex-1">
        {navItems.map((item) => (
          <Button
            key={item.value}
            variant={activeTab === item.value ? "secondary" : "ghost"}
            className="w-full justify-start"
            onClick={() => setActiveTab(item.value)}
          >
            <div className="flex items-center w-full">
              <div className="flex items-center flex-1">
                {item.icon}
                <span className="ml-3">{item.name}</span>
              </div>
              {item.badge && (
                <Badge 
                  variant="secondary" 
                  className="ml-2 bg-red-100 text-red-700 text-xs min-w-[20px] h-5 flex items-center justify-center"
                >
                  {item.badge > 99 ? "99+" : item.badge}
                </Badge>
              )}
            </div>
          </Button>
        ))}
      </nav>
      <Button
        variant="ghost"
        className="w-full justify-start mt-auto"
        onClick={handleLogout}
      >
        <LogOut className="h-5 w-5" />
        <span className="ml-3">Logout</span>
      </Button>
    </div>
  );
};

export default StudentSidebar;
